import { DetailsList, DetailsRow, IShimmeredDetailsListProps, SelectionMode, Shimmer, ShimmerElementType } from "@fluentui/react";
import styles from './PodShimmeredTable.module.scss';

interface Props extends Pick<IShimmeredDetailsListProps, 'columns' | 'layoutMode' | 'items' | 'groups' | 'groupProps' | 'enableShimmer' | 'shimmerLines'> {
  useInactiveStyle?: boolean;
  onRenderEmptyState?: () => React.ReactNode,
  renderPlaceholder?: () => React.ReactNode
}

/**
 * Variant of a ShimmeredDetailsList with a custom render method for creating
 * shimmered rows with groups intact. Column render definitions must be able to
 * handle undefined values, in order to render the proper number of rows in each
 * group.
 */
const PodShimmeredTable = ({columns, layoutMode, items, groups, groupProps, enableShimmer, shimmerLines, useInactiveStyle, onRenderEmptyState, renderPlaceholder}: Props) => {
  let shimmeredItems = [];
  
  if (!renderPlaceholder) {
    shimmeredItems = !enableShimmer
      ? items
      : Array(shimmerLines ?? 5).fill({});
  }

  return (
    <>
      <DetailsList
        {...{columns, groups, groupProps}}
        items={shimmeredItems}
        selectionMode={SelectionMode.none}
        layoutMode={layoutMode}
        enableUpdateAnimations={true}
        className={`${useInactiveStyle ? styles.useInactiveStyle : ''}${shimmeredItems.length === 0 ? ` ${styles.isEmpty}` : ''}`}
        onRenderRow={(props) => {
          if (!enableShimmer) {
            return (
              <DetailsRow
                item={props?.item}
                itemIndex={props?.itemIndex ?? -1}
                columns={props?.columns}
                group={props?.group}
                groupNestingDepth={props?.groupNestingDepth}
                selectionMode={SelectionMode.none}
                onRenderField={(props, defaultRender) => defaultRender!(props)}
                className={`${styles.row}${props?.item.status === 'DEACTIVATED' ? ` ${styles.inactive}` : ''}`}
                cellStyleProps={{
                  cellExtraRightPadding: 24,
                  cellLeftPadding: 12,
                  cellRightPadding: 8,
                }}
              />
            )
          }

          return (
            <div className={`${styles.shimmerRow} ${groups ? styles.hasGroupSpacer : ''}`}>
              {props?.columns.map((column) => (
                <div
                  key={column.key}
                  style={{
                    flex: column.calculatedWidth,
                    minWidth: (column.calculatedWidth ?? 0) + 20,
                  }}
                >
                  <Shimmer
                    width="100%"
                    shimmerElements={[
                      { type: ShimmerElementType.line, width: '100%', height: 18 },
                    ]}
                  />
                </div>
              ))}
            </div>
          )
        }}
      />
      {
        !enableShimmer && !renderPlaceholder && items.length === 0 && onRenderEmptyState
        ? onRenderEmptyState()
        : null
      }
      {
        !!renderPlaceholder && renderPlaceholder()
      }
    </>
  );
}

export default PodShimmeredTable;